import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useParticipants,
  useLocalParticipant,
  useConnectionState,
  ParticipantTile,
  useRoomContext,
  useChat,
} from "@livekit/components-react";
import { Track, ConnectionState } from "livekit-client";
import "@livekit/components-styles";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Users,
  ShieldCheck,
  MessageSquare,
  MonitorUp,
  MonitorOff,
  Send,
  X,
  ClipboardList,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, useAuth } from "@/context/AuthContext";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { Logo } from "@/components/landing/Logo";
import { AddFriendButton } from "@/components/AddFriendButton";
import { ScenarioCard } from "./Briefing";
import { PRACTICE_TYPES } from "./PracticeSetup";

/* Real-time video call powered by LiveKit.
   - Token is minted server-side (/api/livekit/token) so the shared secret never
     leaves the backend.
   - Room name comes from location.state (Matchmaking + Practice populate it).
     Falls back to a shared demo lobby so single-user testing still works. */

const fmt = (s) => {
  const t = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const ss = t % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${m}:${String(ss).padStart(2, "0")}`;
};

// Hard cap: 60 min. Warning: 55 min. Kept in one place so the toast and the
// auto-end trigger stay in sync.
const SESSION_MAX_SECONDS = 60 * 60;
const SESSION_WARN_AT = 55 * 60;

const ControlButton = ({ onClick, off, danger, active, label, testid, children }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    aria-label={label}
    className={`flex h-14 w-14 items-center justify-center rounded-full transition-[background-color,transform] duration-200 active:scale-95 ${
      danger || active
        ? "bg-[#E52525] text-white hover:bg-[#FF3333] shadow-[0_0_25px_rgba(229,37,37,0.4)]"
        : off
          ? "border border-[#E52525]/40 bg-[#E52525]/20 text-[#E52525]"
          : "bg-white/10 text-white hover:bg-white/20"
    }`}
  >
    {children}
  </button>
);

/* Dialler interface for Camera Off (phone) mode — no video tiles, just a
   pulsing phone, partner name, connection state and a big duration timer.
   Remote audio still flows through <RoomAudioRenderer /> at the root. */
const DiallerView = ({ connLabel, seconds, partnerName, hasPeer, micOn }) => (
  <div
    className="flex h-full flex-col items-center justify-center"
    data-testid="dialler-view"
  >
    <div className="relative flex h-44 w-44 items-center justify-center sm:h-52 sm:w-52">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className="absolute h-28 w-28 rounded-full border border-[#E52525]/40 sm:h-32 sm:w-32"
          initial={{ scale: 1, opacity: 0.5 }}
          animate={{ scale: 2, opacity: 0 }}
          transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.8, ease: "easeOut" }}
        />
      ))}
      <span className="relative flex h-28 w-28 items-center justify-center rounded-full border border-[#E52525]/30 bg-[#E52525]/10 sm:h-32 sm:w-32">
        <motion.span
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Phone size={48} className="text-[#E52525]" />
        </motion.span>
      </span>
    </div>
    <p
      data-testid="dialler-partner-name"
      className="mt-6 max-w-[260px] truncate text-lg font-semibold text-white"
    >
      {hasPeer ? partnerName : "Waiting for partner…"}
    </p>
    <p data-testid="dialler-status" className="mt-1 text-sm font-light text-zinc-400">
      {connLabel}
      {micOn ? "" : " · Muted"}
    </p>
    <p
      data-testid="dialler-timer"
      className="mt-5 font-heading text-4xl font-black tracking-tight text-white sm:text-5xl"
    >
      {fmt(seconds)}
    </p>
  </div>
);

/* In-call context panel — both reps' scenario cards, read-only. Pure
   overlay: opening/closing it touches no LiveKit state, so audio/video are
   never interrupted. Bottom sheet on mobile, slide-in side panel on sm+. */
const ContextPanel = ({
  open,
  onClose,
  myName,
  partnerName,
  myContext,
  partnerContext,
}) => (
  <>
    <div
      data-testid="context-panel-backdrop"
      onClick={onClose}
      className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
        open ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    />
    <aside
      data-testid="context-panel"
      aria-hidden={!open}
      className={`fixed inset-x-0 bottom-0 z-50 flex max-h-[78vh] flex-col rounded-t-3xl border-t border-white/10 bg-[#111111] transition-transform duration-300 ease-out sm:inset-y-0 sm:left-0 sm:right-auto sm:h-full sm:max-h-none sm:w-[400px] sm:rounded-none sm:border-r sm:border-t-0 ${
        open
          ? "translate-y-0 sm:translate-x-0"
          : "translate-y-full sm:translate-y-0 sm:-translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between px-5 pt-5">
        <h2 className="font-heading text-lg font-bold tracking-tight text-white">
          Call scenarios
        </h2>
        <button
          type="button"
          data-testid="context-panel-close"
          aria-label="Close scenarios"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <X size={16} />
        </button>
      </div>
      <p className="mt-1 px-5 text-xs font-light text-zinc-500">
        Read-only — each card was submitted by that rep before matching.
      </p>
      <div className="mt-4 flex-1 space-y-4 overflow-y-auto px-5 pb-6">
        <ScenarioCard
          testid="panel-your-scenario"
          title="Your Scenario"
          name={myName}
          context={myContext}
          sellerLine={`You sell · ${partnerName} plays the prospect`}
          accent
        />
        <ScenarioCard
          testid="panel-partner-scenario"
          title={`${partnerName}'s Scenario`}
          name={partnerName}
          context={partnerContext}
          sellerLine={`${partnerName} sells · You play the prospect`}
        />
      </div>
    </aside>
  </>
);

// -------------------- Video grid rendered inside <LiveKitRoom> --------------------

function VideoGrid() {
  const participants = useParticipants();


  const room = useRoomContext();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Diagnostic: log the room name + all participant identities every time the
  // list changes. Two devices in the same room should see each other's ids.
  useEffect(() => {
    console.info(
      "[livekit] room=%s connState=%s participants=%o",
      room?.name,
      room?.state,
      participants.map((p) => ({
        identity: p.identity,
        name: p.name,
        isLocal: p.isLocal,
      }))
    );
  }, [room, participants]);

  // Also wire the raw livekit-client events so we can see connect/disconnect
  // even if the react hook doesn't refresh.
  useEffect(() => {
    if (!room) return;
    const onConnected = (p) =>
      console.info("[livekit] participantConnected: identity=%s name=%s", p.identity, p.name);
    const onDisconnected = (p) =>
      console.info("[livekit] participantDisconnected: identity=%s", p.identity);
    const onRoomConnected = () =>
      console.info("[livekit] room CONNECTED name=%s localIdentity=%s", room.name, room.localParticipant?.identity);
    room.on("participantConnected", onConnected);
    room.on("participantDisconnected", onDisconnected);
    room.on("connected", onRoomConnected);
    return () => {
      room.off("participantConnected", onConnected);
      room.off("participantDisconnected", onDisconnected);
      room.off("connected", onRoomConnected);
    };
  }, [room]);

  // Always show the local participant + remotes. Pad with waiting tile if alone.
  const uniqueTracks = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of tracks) {
      const key = `${t.participant.identity}:${t.source}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  }, [tracks]);

  const soloWaiting = participants.length <= 1;

  return (
    <div
      data-testid="video-grid"
      className="grid h-full min-h-0 grid-cols-1 grid-rows-2 gap-3 md:grid-cols-2 md:grid-rows-1"
    >
      {uniqueTracks.map((t, i) => (
        <div
          key={`${t.participant.identity}-${t.source}-${i}`}
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-black"
          data-testid={
            t.participant.isLocal ? "local-video" : `remote-video-${t.participant.identity}`
          }
        >
          <ParticipantTile trackRef={t} className="!h-full !w-full !bg-black" />
        </div>
      ))}
      {soloWaiting && (
        <div
          data-testid="waiting-tile"
          className="relative flex flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-dashed border-white/15 bg-black/50 text-center"
        >
          <Users size={28} className="text-zinc-500" />
          <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-zinc-400">
            Waiting for partner
          </p>
          <p className="max-w-xs text-xs text-zinc-500">
            Share the room link or wait for a match. Their video will appear here.
          </p>
        </div>
      )}
    </div>
  );
}

function ChatPanel({ open, onClose, participants }) {
  const { chatMessages, send, isSending } = useChat();
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  // Auto-scroll to newest when messages arrive or the panel opens.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, chatMessages.length]);

  const nameFor = (identity) => {
    const p = participants.find((pp) => pp.identity === identity);
    return p?.name || p?.identity?.slice(0, 8) || "Rep";
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if (!value || isSending) return;
    try {
      await send(value);
      setText("");
    } catch {
      toast.error("Could not send message");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="chat-panel"
      className="pointer-events-auto absolute right-4 top-20 z-30 flex h-[65vh] max-h-[520px] w-[320px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#141414]/95 shadow-2xl backdrop-blur-xl"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <MessageSquare size={15} className="text-[#E52525]" />
          <span className="text-sm font-bold text-white">In-call chat</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="chat-close-btn"
          className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>

      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
      >
        {chatMessages.length === 0 ? (
          <p className="pt-6 text-center text-xs text-zinc-500">
            Say hi to your practice partner — messages stay in this call.
          </p>
        ) : (
          chatMessages.map((m) => {
            const isSelf = m.from?.isLocal;
            const senderName = isSelf ? "You" : m.from?.name || nameFor(m.from?.identity);
            return (
              <div
                key={m.id || `${m.timestamp}-${m.message}`}
                data-testid={`chat-msg-${isSelf ? "self" : "peer"}`}
                className={`flex flex-col ${isSelf ? "items-end" : "items-start"}`}
              >
                <span className="text-[10px] font-medium text-zinc-500">
                  {senderName}
                </span>
                <span
                  className={`mt-0.5 max-w-[80%] rounded-2xl px-3 py-1.5 text-[13px] leading-snug ${
                    isSelf
                      ? "bg-[#E52525] text-white"
                      : "bg-white/8 text-zinc-100"
                  }`}
                >
                  {m.message}
                </span>
              </div>
            );
          })
        )}
      </div>

      <form
        onSubmit={submit}
        className="flex items-center gap-2 border-t border-white/10 px-3 py-3"
      >
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message"
          maxLength={500}
          className="flex-1 rounded-full border border-white/10 bg-black/40 px-3.5 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-[#E52525]/50 focus:outline-none"
        />
        <button
          type="submit"
          data-testid="chat-send-btn"
          disabled={!text.trim() || isSending}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E52525] text-white transition-colors hover:bg-[#c81f1f] disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Send message"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}

function CallShell({
  initialDuration,
  initialCamOn = true,
  onEnd,
  ending,
  onPartnerName,
  sessionId,
  diallerMode = false,
  contextEnabled = false,
  myName = "You",
  myContext = null,
  partnerContext = null,
  partnerNameProp = "",
  practiceLabel = "Mock call",
}) {
  const [seconds, setSeconds] = useState(initialDuration);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(initialCamOn);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { localParticipant, isScreenShareEnabled } = useLocalParticipant();
  const participants = useParticipants();
  const connState = useConnectionState();
  const room = useRoomContext();
  const { chatMessages } = useChat();
  const lastSeenCountRef = useRef(0);

  const peerSeenRef = useRef(false);
  const partnerPatchedRef = useRef(false);

  // Remote peer (if any) — used by the partner name pill + Add Friend button
  // in the top bar. We recompute on every participants change.
  const remotePeer = participants.find((p) => !p.isLocal) || null;
  const remotePeerName = remotePeer?.name || remotePeer?.identity || "";
  const remotePeerId = remotePeer?.identity || "";

  // Bubble the remote participant's public identity (their @username) up to
  // the outer container so we can persist it as `partner_name` on the session,
  // along with their user_id (LiveKit `identity` == supabase_sub) for rematch.
  //
  // Two separate guards, decoupled on purpose:
  //   - `peerSeenRef`  fires the outer callback ONCE (used by endCall fallback)
  //   - `partnerPatchedRef` guards the PATCH to the backend, which requires
  //      `sessionId` to be resolved. If the peer appears before /sessions/start
  //      finishes, we retry on the next effect run when `sessionId` shows up.
  useEffect(() => {
    const remote = participants.find((p) => !p.isLocal);
    if (!remote || !(remote.name || remote.identity)) return;
    const name = remote.name || remote.identity;
    const uid = remote.identity;

    if (!peerSeenRef.current) {
      peerSeenRef.current = true;
      onPartnerName?.(name, uid);
    }

    if (!partnerPatchedRef.current && sessionId) {
      partnerPatchedRef.current = true;
      console.info(
        "[partner-report] PATCH /sessions/%s/partner name=%s uid=%s",
        sessionId,
        name,
        uid
      );
      api
        .patch(`/sessions/${sessionId}/partner`, {
          partner_name: name,
          partner_user_id: uid,
        })
        .then((r) => {
          console.info("[partner-report] PATCH ok", r?.data);
        })
        .catch((err) => {
          // Do NOT swallow — surface loudly so we notice regressions. The
          // ref is reset so a later effect run can retry.
          partnerPatchedRef.current = false;
          const status = err?.response?.status;
          const body = err?.response?.data;
          console.error(
            "[partner-report] PATCH failed status=%s body=%o err=%o",
            status,
            body,
            err
          );
          toast.error(
            `Could not save partner link (status ${status ?? "?"}). Rematch may be disabled.`
          );
        });
    }
  }, [participants, onPartnerName, sessionId]);

  // Unread counter: increments for every incoming message received while the
  // chat panel is closed; resets to 0 whenever the panel opens.
  useEffect(() => {
    if (chatOpen) {
      lastSeenCountRef.current = chatMessages.length;
      setUnread(0);
      return;
    }
    const delta = chatMessages.length - lastSeenCountRef.current;
    if (delta > 0) {
      // Only count messages that came in from the peer, not our own.
      let incoming = 0;
      for (let i = lastSeenCountRef.current; i < chatMessages.length; i++) {
        if (!chatMessages[i]?.from?.isLocal) incoming++;
      }
      lastSeenCountRef.current = chatMessages.length;
      if (incoming) setUnread((u) => u + incoming);
    }
  }, [chatMessages.length, chatOpen]);

  // Tick every second while the call is on-screen. Cleanup handles unmount and
  // React 18 StrictMode's double-invoke correctly — the previous `startedRef`
  // guard actually froze the timer at 00:00 because useRef persists across the
  // remount, so the second effect would early-return before creating a new
  // interval.
  useEffect(() => {
    const iv = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  // ---- Session time limit ----
  // 60-minute hard cap with a 5-minute warning at 55:00. We flip a ref, not
  // state, so the warning toast fires exactly once even under StrictMode.
  const warnedRef = useRef(false);
  const autoEndFiredRef = useRef(false);
  useEffect(() => {
    if (!warnedRef.current && seconds >= SESSION_WARN_AT) {
      warnedRef.current = true;
      toast.info("This session ends in 5 minutes.", { duration: 8000 });
    }
    if (seconds >= SESSION_MAX_SECONDS && !autoEndFiredRef.current && !ending) {
      autoEndFiredRef.current = true;
      handleEnd();
    }
  }, [seconds]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMic = async () => {
    const next = !micOn;
    setMicOn(next);
    try {
      await localParticipant.setMicrophoneEnabled(next);
    } catch (e) {
      toast.error("Could not toggle mic");
      setMicOn(!next);
    }
  };

  const toggleCam = async () => {
    const next = !camOn;
    setCamOn(next);
    try {
      await localParticipant.setCameraEnabled(next);
    } catch (e) {
      toast.error("Could not toggle camera");
      setCamOn(!next);
    }
  };

  /* Screen share (Camera On only). `isScreenShareEnabled` from
     useLocalParticipant is the single source of truth — it flips when the
     track ends, so the browser's native "Stop sharing" bar updates the
     button too. shareTogglingRef serialises clicks so a double-tap can never
     publish twice or leave the button inconsistent. */
  const shareOn = !!isScreenShareEnabled;
  const shareTogglingRef = useRef(false);
  const toggleShare = async () => {
    if (shareTogglingRef.current) return;
    if (!shareOn && typeof navigator.mediaDevices?.getDisplayMedia !== "function") {
      toast.error("Screen sharing isn't supported in this browser");
      return;
    }
    shareTogglingRef.current = true;
    try {
      await localParticipant.setScreenShareEnabled(!shareOn);
    } catch (e) {
      toast.error(
        e?.name === "NotAllowedError"
          ? "Screen sharing was blocked or cancelled"
          : "Could not start screen sharing"
      );
    } finally {
      shareTogglingRef.current = false;
    }
  };

  const handleEnd = async () => {
    try {
      await room?.disconnect();
    } catch { /* ignore */ }
    onEnd(seconds);
  };

  const connLabel = {
    [ConnectionState.Connected]: "Connected",
    [ConnectionState.Connecting]: "Connecting…",
    [ConnectionState.Reconnecting]: "Reconnecting…",
    [ConnectionState.Disconnected]: "Disconnected",
  }[connState] || connState;

  return (
    <main
      className="relative flex h-screen flex-col overflow-hidden bg-[#0A0A0A] font-body text-white"
      data-testid="video-call-page"
    >
      {ending && <FullScreenLoader label="Wrapping up your session…" />}

      {/* SalesPit wordmark — top-center, non-interactive so it never covers
          video tiles or controls. Full opacity with a brighter "Pit" red
          (#FF3030) overridden via descendant selectors so the shared Logo
          component itself stays untouched. */}
      <div
        aria-hidden="true"
        data-testid="in-call-wordmark"
        className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-center [&_span:first-child]:!text-white [&_span:last-child]:!text-[#FF3030]"
      >
        <Logo className="text-2xl sm:text-[1.75rem]" />
      </div>

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <span
            data-testid="call-timer"
            className="flex items-center gap-2 rounded-full bg-black/60 px-3.5 py-2 text-sm font-semibold text-white backdrop-blur-sm"
          >
            <span className="sp-live-dot h-2 w-2 rounded-full bg-[#E52525]" />
            {fmt(seconds)}
          </span>
          <span
            data-testid="connection-status"
            className={`hidden items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold uppercase tracking-wide sm:flex ${
              connState === ConnectionState.Connected
                ? "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E]"
                : "border-[#F5A524]/30 bg-[#F5A524]/10 text-[#F5A524]"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                connState === ConnectionState.Connected ? "bg-[#22C55E]" : "bg-[#F5A524]"
              }`}
            />
            {connLabel}
          </span>

          {remotePeerId && (
            <div
              data-testid="partner-pill"
              className="flex items-center gap-2 rounded-full bg-white/5 pl-3 pr-1.5 py-1 text-sm font-medium text-white ring-1 ring-white/10"
            >
              <span data-testid="partner-username" className="max-w-[140px] truncate">
                {remotePeerName}
              </span>
              <AddFriendButton
                peerUserId={remotePeerId}
                peerUsername={remotePeerName}
                testid="call-add-friend-btn"
                className="!py-1"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span
            data-testid="security-badge"
            className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-zinc-200"
          >
            <ShieldCheck size={14} />
            Encrypted
          </span>
        </div>
      </div>

      {/* Video grid / Dialler (Camera Off mode never renders video tiles) */}
      <div className="min-h-0 flex-1 px-4 pb-2 sm:px-6">
        {diallerMode ? (
          <DiallerView
            connLabel={connLabel}
            seconds={seconds}
            partnerName={remotePeerName || partnerNameProp || "Your partner"}
            hasPeer={!!remotePeer}
            micOn={micOn}
          />
        ) : (
          <VideoGrid />
        )}
      </div>

      {/* In-call chat panel (renders only when open) */}
      <ChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        participants={participants}
      />

      {/* Controls + bottom info area (context button + practice type + timer) */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-6 sm:px-8">
        {/* bottom-left: context button + info pill (matchmaking mocks only) */}
        <div className="flex items-center gap-2 justify-self-start">
          {contextEnabled && (
            <>
              <button
                type="button"
                data-testid="call-context-btn"
                aria-label="View call scenarios"
                onClick={() => setCtxOpen(true)}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white transition-[background-color,transform] duration-200 hover:bg-white/20 active:scale-95"
              >
                <ClipboardList size={20} />
              </button>
              <span
                data-testid="call-info-bar"
                className="hidden items-center gap-2 rounded-full bg-black/60 px-3.5 py-2 text-xs font-medium text-zinc-300 backdrop-blur-sm sm:flex"
              >
                <span className="max-w-[140px] truncate">{practiceLabel}</span>
                <span aria-hidden="true" className="text-zinc-600">·</span>
                <span data-testid="call-info-duration">{fmt(seconds)}</span>
              </span>
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-3 sm:gap-4">
          {!diallerMode && (
            <ControlButton
              testid="camera-toggle-btn"
              off={!camOn}
              onClick={toggleCam}
              label="Toggle camera"
            >
              {camOn ? <Video size={22} /> : <VideoOff size={22} />}
            </ControlButton>
          )}

          {!diallerMode && (
            <ControlButton
              testid="screenshare-toggle-btn"
              active={shareOn}
              onClick={toggleShare}
              label={shareOn ? "Stop sharing screen" : "Share screen"}
            >
              {shareOn ? <MonitorOff size={22} /> : <MonitorUp size={22} />}
            </ControlButton>
          )}

          <ControlButton
            testid="mic-toggle-btn"
            off={!micOn}
            onClick={toggleMic}
            label="Toggle microphone"
          >
            {micOn ? <Mic size={22} /> : <MicOff size={22} />}
          </ControlButton>

          {!diallerMode && (
            <div className="relative">
              <ControlButton
                testid="chat-toggle-btn"
                off={!chatOpen}
                onClick={() => setChatOpen((v) => !v)}
                label={chatOpen ? "Close chat" : "Open chat"}
              >
                <MessageSquare size={22} />
              </ControlButton>
              {unread > 0 && !chatOpen && (
                <span
                  data-testid="chat-unread-badge"
                  className="pointer-events-none absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E52525] px-1.5 text-[10px] font-bold text-white ring-2 ring-[#0A0A0A]"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </div>
          )}

          <ControlButton
            testid="end-call-btn"
            danger
            onClick={handleEnd}
            label={ending ? "Ending call…" : "End call"}
          >
            <PhoneOff size={22} />
          </ControlButton>
        </div>

        {/* balance cell keeps controls centred */}
        <div aria-hidden="true" />
      </div>

      {/* In-call context panel — pure overlay, never touches A/V state */}
      {contextEnabled && (
        <ContextPanel
          open={ctxOpen}
          onClose={() => setCtxOpen(false)}
          myName={myName}
          partnerName={remotePeerName || partnerNameProp || "Your partner"}
          myContext={myContext}
          partnerContext={partnerContext}
        />
      )}

      <RoomAudioRenderer />
    </main>
  );
}

// -------------------- Outer container: fetch token, mount LiveKitRoom --------------------

export default function VideoCall() {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode === "friend" ? "friend" : "matchmaking";
  // Server-assigned role for matchmaking calls (rep opens, prospect listens).
  // Absent on friend calls — they never show the opening indicator.
  const yourRole = location.state?.yourRole || null;  const stateRoom = location.state?.room;
  // Device IDs chosen on the Pre-Call screen. If missing we fall back to
  // LiveKit's defaults.
  const videoDeviceId = location.state?.videoDeviceId || "";
  const audioDeviceId = location.state?.audioDeviceId || "";
  // Camera preference. Matchmaking calls carry it from Practice Setup via
  // the Briefing screen; friend calls carry the invite's call_mode from the
  // Friends page / invite toast. Defaults to ON when absent (legacy links).
  const cameraOn = location.state?.cameraOn !== false;
  // Camera Off = dedicated audio-only dialler interface (both matchmaking
  // and friend calls share the same phone simulation).
  const diallerMode = !cameraOn;

  // ---- Context cards for the in-call panel (matchmaking only) ----
  // Primary source: nav state from the Briefing screen. Fallbacks: the
  // sessionStorage match payload (partner) + localStorage (own), so the
  // panel still works if state is lost.
  const { user } = useAuth();
  const myName = user?.name || user?.username || "You";
  const storedMatch = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("salespit.matched_payload") || "null");
    } catch {
      return null;
    }
  })();
  const myContext =
    location.state?.myContext ??
    (() => {
      try {
        return JSON.parse(localStorage.getItem("salespit.practice_context") || "null");
      } catch {
        return null;
      }
    })();
  const partnerContext =
    location.state?.partnerContext ?? storedMatch?.partner_context ?? null;
  const partnerNameProp =
    location.state?.partnerName ?? storedMatch?.partner_name ?? "";
  const practiceLabel =
    PRACTICE_TYPES.find((t) => t.key === myContext?.practice_type)?.title ||
    "Mock call";

  // Room name comes from location.state — both matchmaking (server-paired)
  // and friend flows populate it. If we ever land here without a room, that's
  // an invalid deep-link → bounce to homepage rather than silently dumping
  // the user into a shared lobby (the old behaviour meant two real users
  // could never reliably meet).
  const [roomName] = useState(() => stateRoom || "");

  useEffect(() => {
    if (!stateRoom) {
      toast.error("No active room — start from Home to find a partner.");
      navigate("/homepage", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [serverUrl, setServerUrl] = useState("");
  const [token, setToken] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState(null);
  const partnerNameRef = useRef(null);
  const partnerUserIdRef = useRef(null);

  // Opening indicator (Step 4): shown on call entry when a role was
  // assigned — "YOUR TURN" for the rep, "LISTEN" for the prospect — and
  // stays until the user dismisses it with the X (no auto-hide). Applies to
  // matchmaking AND rematch calls (rematches rotate roles fairly); direct
  // friend calls (no role) never see it. The banner is per-client local
  // state: dismissing it never affects the partner's banner or the roles.
  const [showRoleBanner, setShowRoleBanner] = useState(!!yourRole);

  useEffect(() => {
    if (!roomName) return;
    let alive = true;
    (async () => {
      try {
        // 1) Kick off server-side recording + create the session row.
        //    We do this BEFORE minting the participant token so egress is
        //    running by the time this user's tracks are published.
        const startRes = await api.post("/sessions/start", {
          room_name: roomName,
          role: yourRole || undefined,
          call_mode: cameraOn ? "video" : "phone",
        });
        if (!alive) return;
        setSessionId(startRes.data.session_id);

        // 2) Mint the LiveKit participant token.
        const tokRes = await api.post("/livekit/token", { room_name: roomName });
        if (!alive) return;
        setServerUrl(tokRes.data.server_url);
        setToken(tokRes.data.token);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not join room");
      }
    })();
    return () => {
      alive = false;
    };
  }, [roomName]);

  const endCall = async (durationSeconds) => {
    if (ending) return;
    setEnding(true);
    try {
      const res = await api.post("/sessions/complete", {
        session_id: sessionId || undefined,
        duration_seconds: durationSeconds,
        // Peer's live LiveKit display name (@username) and user_id — the
        // Session Review page needs both so it can offer Rematch.
        partner_name: partnerNameRef.current || undefined,
        partner_user_id: partnerUserIdRef.current || undefined,
      });
      const sid = res?.data?.session_id;
      if (sid) {
        navigate(`/review/${sid}`, { replace: true, state: { fresh: true, mode } });
        return;
      }
      throw new Error("No session_id returned");
    } catch {
      toast.error("Could not save your session. Please try again.");
      setEnding(false);
    }
  };

  if (error)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0A0A] text-white">
        <p className="text-zinc-400">{error}</p>
        <button
          onClick={() => navigate("/homepage")}
          className="mt-4 rounded-full bg-[#E52525] px-5 py-2 text-sm font-semibold text-white"
        >
          Back to Home
        </button>
      </div>
    );
  if (!token || !serverUrl) return <FullScreenLoader label="Joining room…" />;

  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      connect
      video={cameraOn ? (videoDeviceId ? { deviceId: videoDeviceId } : true) : false}
      audio={audioDeviceId ? { deviceId: audioDeviceId } : true}
      data-lk-theme="default"
      onError={(e) => {
        console.error("[livekit] LiveKitRoom onError", e);
        toast.error(e?.message || "Video call error");
      }}
      onDisconnected={(reason) => {
        console.warn("[livekit] LiveKitRoom disconnected reason=%o", reason);
      }}
      onMediaDeviceFailure={(kind) => {
        console.error("[livekit] media device failure kind=%s", kind);
        toast.error(
          `Couldn't access your ${kind || "camera / mic"}. Check browser permissions.`
        );
      }}
    >
      {/* Opening indicator — rep opens, prospect listens (Step 4) */}
      {showRoleBanner && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="role-banner"
          data-role={yourRole}
          className={`fixed left-1/2 top-20 z-[60] -translate-x-1/2 rounded-2xl border px-8 py-5 text-center shadow-[0_8px_40px_rgba(0,0,0,0.55)] backdrop-blur-md ${
            yourRole === "rep"
              ? "border-[#E52525]/50 bg-[#140A0A]/90"
              : "border-slate-400/30 bg-[#0D0F12]/90"
          }`}
        >
          <button
            type="button"
            data-testid="role-banner-close"
            aria-label="Dismiss role prompt"
            onClick={() => setShowRoleBanner(false)}
            className={`absolute right-2.5 top-2.5 rounded-full p-1 transition-colors ${
              yourRole === "rep"
                ? "text-[#FF5A5A]/70 hover:bg-[#E52525]/15 hover:text-[#FF5A5A]"
                : "text-slate-400/70 hover:bg-white/10 hover:text-slate-200"
            }`}
          >
            <X size={14} />
          </button>
          <p
            className={`text-sm font-black uppercase tracking-[0.22em] ${
              yourRole === "rep" ? "text-[#FF5A5A]" : "text-slate-300"
            }`}
          >
            {yourRole === "rep" ? "You start" : "Your turn to listen"}
          </p>
          <p className="mt-1 text-base font-semibold text-white">
            {yourRole === "rep"
              ? "Open the conversation"
              : "Let the Rep open"}
          </p>
        </motion.div>
      )}
      <CallShell
        initialDuration={0}
        initialCamOn={cameraOn}
        onEnd={endCall}
        ending={ending}
        sessionId={sessionId}
        diallerMode={diallerMode}
        contextEnabled={mode === "matchmaking"}
        myName={myName}
        myContext={myContext}
        partnerContext={partnerContext}
        partnerNameProp={partnerNameProp}
        practiceLabel={practiceLabel}
        onPartnerName={(name, userId) => {
          partnerNameRef.current = name;
          partnerUserIdRef.current = userId || null;
        }}
      />
    </LiveKitRoom>
  );
}
